import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import get from 'lodash/get';
import set from 'lodash/set';
import addressService from './addressService';
import * as addressConsts from './addressConsts';
import classes from './AddressSearch.module.scss';
import AutoComplete from '../AutoCompleteWrapper/AutoCompleteWrapper';
import { useWait } from 'react-wait';
import AddressManual from './AddressManual';
import { Notification, FormRadioGroup, Button } from '@snsw/react-component-library';

const AddressSearch = (props, ref) => {
  const { values, setFieldValue, name, initialValues, international, countries } = props;
  const { startWaiting, endWaiting } = useWait();
  const [validated, setValidated] = useState(get(initialValues, `${name}`, undefined));
  const [partTouched, setPartTouched] = useState(false);
  const [addressLineValue, setAddressLineValue] = useState(false);
  const [suburbValue, setSuburbValue] = useState(false);
  const [postcodeValue, setPostcodeValue] = useState(false);
  const [stateValue, setStateValue] = useState(false);
  const initAddressType = !!get(values, `${name}.international`, undefined)
    ? addressConsts.AddressTypeInternational
    : addressConsts.AddressTypeAustralian;
  const [addressType, setAddressType] = useState(initAddressType);

  useEffect(() => {
    return () => {
      if (props.onLoading) props.onLoading(false);
    };
  }, []);

  useEffect(() => {
    if (props.onChange) {
      const valuesChange = {
        ...values,
      };
      valuesChange[name].international = addressType === addressConsts.AddressTypeInternational;
      props.onChange(valuesChange);
    }

    const manual = get(values, `${name}.enterManually`, false);
    const details = manual
      ? get(values, `${name}.addressValidationResult`, undefined)
      : get(values, `${name}.details`, undefined);

    if (props.onDetailsRetrieved && details) {
      props.onDetailsRetrieved(values);
    }
    if (props.onLoading && details) {
      props.onLoading(false);
    }
    const erroredFields = validateFields()[name];
    const errors = erroredFields ? Object.keys(erroredFields) : [];
    const isValid =
      !!details || (addressType === addressConsts.AddressTypeInternational && !errors.length);
    if (props.onValidationChange) {
      props.onValidationChange(isValid);
    }
    if (props.onWarningChange) {
      if (!isValid) {
        props.onWarningChange(false);
      } else {
        const manual = get(values, `${name}.enterManually`, false);
        if (manual) {
          let matchQuality = get(values, `${name}.details.Address.MatchQuality`, false);
          props.onWarningChange(matchQuality !== 'full');
        } else {
          props.onWarningChange(false);
        }
      }
    }
  }, [values, addressType]);

  const addressLine = get(values, `${name}.addressLine`, undefined);

  const suburb = get(values, `${name}.suburb`, undefined);

  const state = get(values, `${name}.state`, undefined);

  const postcode = get(values, `${name}.postcode`, undefined);

  const country = get(values, `${name}.country`, undefined);

  useEffect(() => {
    if (props.autoValidate) {
      if (ref) {
        ref.current.validateAll();
      }
    }
  }, [addressLine, suburb, state, postcode, country, addressType]);

  const setValidating = v => {
    if (props.onValidating) {
      props.onValidating(v);
    }
  };

  const validateAll = () => {
    setValidating(true);
    const errors = validateFields();
    if (Object.keys(errors).length) {
      if (props.onErrors) {
        props.onErrors(errors);
      }
      setValidating(false);
    } else {
      validatePart().then(errors => {
        if (
          props.onErrors &&
          addressType === addressConsts.AddressTypeAustralian &&
          errors.manuallyEnterAddressMatchQuality !== 'full'
        ) {
          props.onErrors({
            matchQuality: errors.manuallyEnterAddressMatchQuality,
          });
        }
        setValidating(false);
      });
    }
  };

  const showErrorForEmptyAutocompleteAddress = lookup => {
    return (
      !lookup ||
      lookup.value === addressConsts.AddressValueUseAddressAsEntered ||
      lookup.id === addressConsts.AutocompleteAddressDoesNotExist
    );
  };

  const validateFields = () => {
    let errors = {};

    if (manual || addressType === addressConsts.AddressTypeInternational) {
      const addressLine = get(values, `${name}.addressLine`, '');
      if (!addressLine) {
        set(errors, `${name}.addressLine`, 'Required');
      }
      const suburb = get(values, `${name}.suburb`, '');
      if (!suburb) {
        set(errors, `${name}.suburb`, 'Required');
      }

      const state = get(values, `${name}.state`, '');
      if (!state) {
        set(errors, `${name}.state`, 'Required');
      }

      if (international && addressType === addressConsts.AddressTypeInternational) {
        const country = get(values, `${name}.country`, '');
        if (!country) {
          set(errors, `${name}.country`, 'Required');
        }
      }

      const postcode = get(values, `${name}.postcode`, '');
      if (!postcode) {
        set(errors, `${name}.postcode`, 'Required');
      }
      if (international && addressType === addressConsts.AddressTypeInternational) {
        if (!/^[0-9a-zA-Z]{3,7}$/i.test(postcode)) {
          set(errors, `${name}.postcode`, 'Invalid postcode');
        }
      } else {
        if (!/^[0-9]{4}$/i.test(postcode)) {
          set(errors, `${name}.postcode`, 'Invalid postcode');
        }
      }
    } else {
      const lookup = get(values, `${name}.lookupValue`, '');

      if (showErrorForEmptyAutocompleteAddress(lookup)) {
        set(errors, `${name}.lookupValue`, 'Please enter address before continuing.');
      }
    }

    if (Object.keys(errors).length) {
      setPartTouched(true);
      clearPartValidationResult();
    }

    if (props.onErrors) {
      props.onErrors(errors);
    }
    return errors;
  };

  const clearPartValidationResult = () => {
    setFieldValue(`${name}.addressValidationResult`, undefined, false);
  };

  const validatePart = async () => {
    let partValues = get(values, `${name}`, {});
    let manuallyEnterAddressChanged = undefined;
    let manuallyEnterAddressMatchQuality = undefined;

    if (addressType === addressConsts.AddressTypeAustralian) {
      manuallyEnterAddressChanged = false;
      manuallyEnterAddressMatchQuality = get(
        values,
        `${name}.addressValidationResult.Address.MatchQuality`,
        undefined
      );

      const manual = get(values, `${name}.enterManually`, false);
      if (manual) {
        const addressLine = get(values, `${name}.addressLine`, '');
        const addressLineValidated = get(validated, `addressLine`, '');

        const suburb = get(values, `${name}.suburb`, '');
        const suburbValidated = get(validated, `suburb`, '');

        const state = get(values, `${name}.state`, '');
        const stateValidated = get(validated, `state`, '');

        const country = get(values, `${name}.country`, '');
        const countryValidated = get(validated, `country`, '');

        const postcode = get(values, `${name}.postcode`, '');
        const postcodeValidated = get(validated, `postcode`, '');

        if (
          partTouched ||
          addressLine !== addressLineValidated ||
          suburb !== suburbValidated ||
          state.value !== (stateValidated || {}).value ||
          postcode !== postcodeValidated ||
          country !== countryValidated
        ) {
          const query =
            addressLine +
            ' ' +
            suburb +
            ' ' +
            (typeof state === 'string' ? state : state.value) +
            ' ' +
            postcode;

          if (props.onLoading) {
            props.onLoading(true);
          }
          const addressValidationResp = await addressService.validateAddress(
            query,
            props.validationUrl,
            props.apiKeyName
          );
          if (addressValidationResp && addressValidationResp.data) {
            partValues = {
              ...partValues,
              ['addressValidationResult']: addressValidationResp.data,
            };

            manuallyEnterAddressMatchQuality =
              (addressValidationResp.data.Address || {}).MatchQuality || 'none';

            setFieldValue(`${name}.addressValidationResult`, addressValidationResp.data);

            setValidated({
              addressLine,
              suburb,
              state,
              postcode,
              country,
            });

            manuallyEnterAddressChanged = true;
          }
        }
      }
    }

    setPartTouched(false);

    return {
      manuallyEnterAddressChanged,
      manuallyEnterAddressMatchQuality,
      partValues,
    };
  };

  useImperativeHandle(ref, () => {
    return {
      validateAll: async () => await validateAll(),
      validateFields,
      validatePart: async () => await validatePart(),
    };
  });

  const [manual, setManual] = useState(get(values, `${name}.enterManually`, false));

  const loadOptions = async (query, callback) => {
    const response = await addressService.searchAddress(query, props.searchUrl);
    callback(response.data);
  };

  const onSelect = async selected => {
    if (!selected) {
      return;
    }
    setFieldValue(`${name}.details`, undefined);
    if (props.onSelect) {
      props.onSelect(selected);
    }

    startWaiting('pageOverlay');

    if (selected.value === addressConsts.AddressValueUseAddressAsEntered) {
      manuallyEnterAddress(true);
    } else {
      if (props.onLoading) {
        props.onLoading(true);
      }
      const response = await addressService.getAddressDetailsById(
        selected.value,
        props.addressDetailUrl
      );

      if (response.data.Address.UniqueId === addressConsts.AutocompleteAddressDoesNotExist) {
        manuallyEnterAddress(true);
      } else {
        setFieldValue(`${name}.details`, response.data);
      }
    }

    endWaiting('pageOverlay');
  };

  const onClear = () => {
    // clear values for Manual entered address when switching back to autocomplete address
    setFieldValue(`${name}.lookupValue`, '');
    setFieldValue(`${name}.details`, undefined);
    setFieldValue(`${name}.addressLine`, '');
    setFieldValue(`${name}.suburb`, '');
    setFieldValue(`${name}.state`, '');
    setFieldValue(`${name}.postcode`, '');
    setFieldValue(`${name}.country`, '');
  };

  const manuallyEnterAddress = enterManually => {
    setFieldValue(`${name}.enterManually`, enterManually);
    setManual(enterManually);
    if (props.onManualChange) {
      props.onManualChange(enterManually);
    }
    onClear();
  };

  const manuallyEnteredAddressMatchQuality = get(
    values,
    `${name}.addressValidationResult.Address.MatchQuality`,
    undefined
  );

  const label = props.labelComp
    ? props.labelComp
    : props.label || props.label === null
    ? props.label
    : 'Address';
  const placeholder = props.placeholder || (typeof label === 'string' ? label : 'Address');

  return (
    <div className={classes.addressPart}>
      <div className={classes.titleContainer}>
        <div className={classes.titleAndHelp}>
          <div className={classes.addressLabel}>{label}</div>
          <small>{props.helpMessage}</small>
        </div>
        {addressType === addressConsts.AddressTypeAustralian ? (
          <div className={classes.manualToggleCont}>
            <a
              href={'#'}
              onClick={e => {
                e.preventDefault();
                manuallyEnterAddress(!manual);
              }}
            >
              {manual ? 'Address lookup' : 'Enter manually'}
            </a>
          </div>
        ) : null}
      </div>
      {international && (
        <FormRadioGroup
          className={classes.intlCont}
          onChange={v => {
            manuallyEnterAddress(v === addressConsts.AddressTypeInternational);
            setAddressType(v);
          }}
          options={[
            {
              value: addressConsts.AddressTypeAustralian,
              label: 'Australian',
            },
            {
              value: addressConsts.AddressTypeInternational,
              label: 'International',
            },
          ]}
          initialValue={initAddressType}
          value={addressType}
          onSelectionChange={v => {
            manuallyEnterAddress(v === addressConsts.AddressTypeInternational);
            setAddressType(v);
          }}
        />
      )}
      <div>
        {manual || addressType === addressConsts.AddressTypeInternational ? (
          props.customManual ?? (
            <>
              <AddressManual
                {...props}
                international={
                  international && addressType === addressConsts.AddressTypeInternational
                }
                placeholder={placeholder}
                showErrorsClass={props.showErrors ? classes.errorField : ''}
                onCountryChange={v => {
                  setFieldValue(`${name}.country`, v);
                }}
                countries={countries}
              />
              {manuallyEnteredAddressMatchQuality !== undefined &&
                manuallyEnteredAddressMatchQuality !== addressConsts.MatchQualityFull && (
                  <Notification
                    variant="warning"
                    title="The address you provided could not be found"
                  >
                    <p>
                      Please click on the <strong key="edit">Continue</strong> button to continue
                      with the application if it is correct.
                    </p>
                  </Notification>
                )}
            </>
          )
        ) : (
          <AutoComplete
            id={props.id}
            placeholder={placeholder}
            name={`${name}.lookupValue`}
            loadOptions={loadOptions}
            onSelect={onSelect}
            onClear={onClear}
            showErrorsClass={props.showErrors ? classes.errorField : ''}
            classNames={`${props.classNames}`}
          />
        )}
      </div>
    </div>
  );
};

const AddressSearchComponent = forwardRef(AddressSearch);
AddressSearchComponent.displayName = 'AddressSearch';
export default AddressSearchComponent;
